import { Card, CardContent } from '@/components/ui/card';
import { BarChart3, TrendingUp, FileText, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NewsReportsSectionSvProps {
  handleViewReports: () => void;
}

const NewsReportsSectionSv = ({ handleViewReports }: NewsReportsSectionSvProps) => {
  return (
  <section className="py-24 bg-white">
    <div className="w-full px-8 md:px-16 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-gray-900">Senaste nyheter & rapporter</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Håll dig uppdaterad med den senaste utvecklingen i Sveriges startup-ekosystem
          </p>
        </div>
        <div className="grid grid-cols-9 gap-8">
          {/* First row: 3 cards, each col-span-3 */}
          <div className="col-span-3 animate-on-scroll">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/80defe76-9423-4628-bf6e-02915adc3612.png"
                  alt="Sweden Startup Ecosystem Report 2025"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/70 to-purple-600/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <BarChart3 className="w-3 h-3 mr-1" />
                    RAPPORT
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Nov 15, 2025
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Sveriges Startup-Ekosystemrapport 2025</h3>
                <p className="text-gray-600 text-sm">Omfattande analys av det svenska startup-landskapet inklusive finansieringstrender, sektoranalys och policyrekommendationer.</p>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-3 animate-on-scroll">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/ff6b06fe-1fc6-45c1-8996-653584c7e69e.png"
                  alt="Why Sweden lost MARLEQ"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/70 to-pink-500/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-violet-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    INSIKTER
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Jun 18, 2025
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Varför Sverige förlorade MARLEQ</h3>
                <p className="text-gray-600 text-sm">Insikter från den globala entreprenören Milo Radulovic om systemiska brister och förbättringar som behövs för svenska startups.</p>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-3 animate-on-scroll">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/80defe76-9423-4628-bf6e-02915adc3612.png"
                  alt="Record Breaking Q1 2025 Funding"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/70 to-blue-500/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-orange-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <FileText className="w-3 h-3 mr-1" />
                    NYHETER
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Mar 8, 2025
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Rekordstark Q1 2025-finansiering</h3>
                <p className="text-gray-600 text-sm">Svenska startups samlade in 2,1 miljarder euro under Q1, en ökning med 47% jämfört med föregående år.</p>
              </CardContent>
            </Card>
          </div>
          {/* Second row: 1 card (col-span-3), 1 double box (col-span-6) */}
          <div className="col-span-3 animate-on-scroll mt-8">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/ff6b06fe-1fc6-45c1-8996-653584c7e69e.png"
                  alt="Sweden has the research"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/70 to-purple-500/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-violet-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    INSIKTER
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Jun 11, 2025
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Sverige har forskningen</h3>
                <p className="text-gray-600 text-sm">Analys av varför Sverige saknar kunder och teknologisk optimism trots starka forskningskapaciteter.</p>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-6 animate-on-scroll mt-8 flex items-center justify-center">
            <div className="w-full h-full bg-white rounded-2xl p-10 flex flex-col items-center justify-center shadow-xl">
              <h3 className="text-2xl lg:text-3xl font-bold mb-4 text-gray-900">Varför vi arbetar med rapporter och policy</h3>
              <p className="text-lg text-gray-700 max-w-2xl text-center">
                Rapporter och policyarbete är avgörande för att driva evidensbaserad förändring i det svenska startup-ekosystemet. Genom att analysera trender, dela insikter och påverka policy hjälper vi till att skapa bättre förutsättningar för innovation, tillväxt och global konkurrenskraft.
              </p>
            </div>
          </div>
        </div>
        <div className="text-center mt-12 animate-on-scroll">
          <Button variant="cyan" className="px-8 py-4 text-lg font-semibold rounded-full" onClick={handleViewReports}>
            Visa alla nyheter & rapporter
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  </section>
  );
};

export default NewsReportsSectionSv;
